import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import StudentService from '../../../services/studentService';

const EditStudentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',  // ✅ Shows existing password
    newPassword: ''       // ✅ For changing password
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadStudent();
  }, [id]);

  const loadStudent = async () => {
    try {
      setLoading(true);
      const response = await StudentService.getStudentDetails(id);
      const student = response.data.student;
      setFormData({
        name: student.name || '',
        email: student.email || '',
        phone: student.phone || '',
        currentPassword: student.current_password || '',  // ✅ Load existing password
        newPassword: ''  // Start empty for new password
      });
    } catch (error) {
      toast.error('Failed to load student details');
      navigate('/admin/students/manage');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    // Validate new password if provided
    if (formData.newPassword && formData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      };
      
      // Only send new password if admin entered something
      if (formData.newPassword && formData.newPassword.trim() !== '') {
        updateData.password = formData.newPassword;
      }
      
      const response = await StudentService.updateStudent(id, updateData);
      
      if (response.data.password_updated) {
        toast.success('Student updated successfully! Password has been changed from "' + formData.currentPassword + '" to "' + formData.newPassword + '"');
      } else {
        toast.success('Student updated successfully! Password remains: ' + formData.currentPassword);
      }
      
      navigate('/admin/students/manage');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to update student';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <Button
          variant="link"
          className="mb-3 text-decoration-none"
          onClick={() => navigate('/admin/students/manage')}
        >
          <FaArrowLeft className="me-2" /> Back to Manage Students
        </Button>

        <Card className="shadow-sm" style={{ maxWidth: '650px', margin: '0 auto' }}>
          <Card.Header className="bg-primary text-white">
            <h4 className="mb-0">
              <FaKey className="me-2" />
              Edit Student: {formData.name}
            </h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <div className="section-header mb-3">
                <h5 className="text-primary">Basic Information</h5>
                <hr />
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter student name"
                  isInvalid={!!errors.name}
                  disabled={saving}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  isInvalid={!!errors.email}
                  disabled={saving}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter 10-digit phone number"
                  isInvalid={!!errors.phone}
                  disabled={saving}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>

              {/* Password Section */}
              <div className="section-header mb-3 mt-4">
                <h5 className="text-primary">Password Information</h5>
                <hr />
              </div>

              {/* ✅ CURRENT PASSWORD - SHOWING OLD PASSWORD */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Current Password 
                  <span className="text-muted ms-2" style={{ fontSize: '12px' }}>
                    (Read Only - Student's existing password)
                  </span>
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.currentPassword}
                    readOnly
                    disabled
                    style={{ backgroundColor: '#f8f9fa', fontFamily: 'monospace' }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    type="button"
                  >
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  <small>This is the student's current password. It cannot be edited directly - use "New Password" field below to change it.</small>
                </Form.Text>
              </Form.Group>

              {/* ✅ NEW PASSWORD FIELD */}
              <Form.Group className="mb-3">
                <Form.Label>
                  New Password 
                  <span className="text-muted ms-2" style={{ fontSize: '12px' }}>
                    (Leave blank to keep current password)
                  </span>
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password (min 6 characters)"
                    isInvalid={!!errors.newPassword}
                    disabled={saving}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    type="button"
                    disabled={saving}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.newPassword}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  <small>Minimum 6 characters. Only enter if you want to change the password.</small>
                </Form.Text>
              </Form.Group>

              {/* Info Alert */}
              <Alert variant="info" className="mt-3">
                <small>
                  <strong>📝 Instructions:</strong>
                  <ul className="mb-0 mt-1">
                    <li><strong>Current Password</strong> - Shows the student's existing password (read-only)</li>
                    <li><strong>New Password</strong> - Enter here only if you want to change the password</li>
                    <li>Email and Phone must be unique across all students</li>
                    <li>When password is changed, student must use the new password to login</li>
                  </ul>
                </small>
              </Alert>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/admin/students/manage')}
                  disabled={saving}
                >
                  <FaTimes className="me-2" /> Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" /> Update Student
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default EditStudentForm;